import { useEffect, useMemo, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase/firebaseConfig';
import {
  computeDisplayDayStreak,
  isStreakAtRisk,
} from '../utils/dayStreak';

const DEFAULT_DAILY_GOAL = 5;

/**
 * Live daily progress + lenient display streak from `users/{uid}`.
 */
export default function useDailyProgress(userId, { enabled = true } = {}) {
  const [dailyProgress, setDailyProgress] = useState({});
  const [dailyMasteredProgress, setDailyMasteredProgress] = useState({});
  const [dailyStudyGoal, setDailyStudyGoal] = useState(DEFAULT_DAILY_GOAL);
  const [loading, setLoading] = useState(Boolean(enabled && userId));
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!userId || !enabled) {
      setLoading(false);
      return undefined;
    }

    const userRef = doc(db, 'users', userId);
    setLoading(true);

    const unsubscribe = onSnapshot(
      userRef,
      (snap) => {
        if (snap.exists()) {
          const data = snap.data();
          setDailyProgress(data.dailyProgress || {});
          setDailyMasteredProgress(data.dailyMasteredProgress || {});
          setDailyStudyGoal(data.dailyStudyGoal || DEFAULT_DAILY_GOAL);
        } else {
          setDailyProgress({});
          setDailyMasteredProgress({});
          setDailyStudyGoal(DEFAULT_DAILY_GOAL);
        }
        setLoading(false);
        setError(null);
      },
      (err) => {
        console.error('useDailyProgress: listener error', err);
        setError(err);
        setLoading(false);
      }
    );

    return unsubscribe;
  }, [userId, enabled]);

  const completedToday = useMemo(() => {
    const today = new Date().toDateString();
    return dailyProgress[today] || 0;
  }, [dailyProgress]);

  const masteredToday = useMemo(() => {
    const today = new Date().toDateString();
    return dailyMasteredProgress[today] || 0;
  }, [dailyMasteredProgress]);

  const displayStreak = useMemo(
    () => computeDisplayDayStreak(dailyProgress),
    [dailyProgress]
  );

  const streakAtRisk = useMemo(
    () => isStreakAtRisk(dailyProgress),
    [dailyProgress]
  );

  return {
    dailyProgress,
    dailyMasteredProgress,
    dailyStudyGoal,
    completedToday,
    masteredToday,
    displayStreak,
    streakAtRisk,
    loading,
    error,
  };
}
